import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useContent } from '@/hooks/useContent';
import { useLanguage } from '@/hooks/useLanguage';
import { incrementAndGetVisitorCount } from '@/services/visitorCountApi';

const STORAGE_KEY = 'visitor_count';
const STORAGE_TIMESTAMP_KEY = 'visitor_count_timestamp';
const INCREMENT_FLAG_KEY = 'visitor_increment_called';
const CACHE_DURATION = 1 * 60 * 60 * 1000; // 24 hours

const VisitorCounter = () => {
  const { t } = useContent();
  const { language } = useLanguage();
  const [displayCount, setDisplayCount] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef<HTMLSpanElement>(null);
  const apiCalledRef = useRef(false);

  // Get cached count from localStorage
  const getCachedCount = (): number | null => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      const timestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < CACHE_DURATION) {
          return parseInt(cached, 10);
        }
      }
    } catch (error) {
      // Silent error handling
    }
    return null;
  };

  // Cache count in localStorage
  const cacheCount = (count: number) => {
    try {
      localStorage.setItem(STORAGE_KEY, count.toString());
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      // Silent error handling
    }
  };

  useEffect(() => {
    // Prevent double increment - check if API was already called in this session
    const incrementFlag = sessionStorage.getItem(INCREMENT_FLAG_KEY);
    
    // Initialize with cached count for instant display
    const cachedCount = getCachedCount();
    if (cachedCount !== null) {
      setDisplayCount(cachedCount);
      setIsVisible(true);
    }

    // Only call API if we haven't called it in this session
    if (!incrementFlag && !apiCalledRef.current) {
      apiCalledRef.current = true;
      // Mark that we've called the API in this session
      sessionStorage.setItem(INCREMENT_FLAG_KEY, 'true');

      // Call API silently in background
      const fetchVisitorCount = async () => {
        try {
          const response = await incrementAndGetVisitorCount();
          
          if (response) {
            const { count, previousCount } = response;
            
            // Cache the new count
            cacheCount(count);
            
            // If we had a cached count, animate from previousCount to count
            // Otherwise, just set the count
            if (cachedCount !== null && previousCount !== count) {
              // Animate from previousCount to count
              const obj = { value: previousCount };
              
              gsap.to(obj, {
                value: count,
                duration: 2,
                ease: 'power2.out',
                onUpdate: function () {
                  const currentValue = Math.floor(obj.value);
                  setDisplayCount(currentValue);
                },
                onComplete: () => {
                  setDisplayCount(count);
                  // Trigger attention animation
                  if (countRef.current) {
                    gsap.to(countRef.current, {
                      scale: 1.2,
                      duration: 0.3,
                      yoyo: true,
                      repeat: 1,
                      ease: 'power2.out',
                    });
                  }
                },
              });
            } else {
              // No animation needed, just set the count
              setDisplayCount(count);
            }
            
            setIsVisible(true);
          } else {
            // API failed, but we already have cached count displayed
            // Just ensure visibility
            if (cachedCount !== null) {
              setIsVisible(true);
            }
          }
        } catch (error) {
          // Silent error handling - if we have cached count, show it
          if (cachedCount !== null) {
            setIsVisible(true);
          }
        }
      };

      fetchVisitorCount();
    } else if (cachedCount !== null) {
      // API was already called, just show cached count
      setIsVisible(true);
    }
  }, []);

  // Don't render if we don't have a count to show (waiting for API and no cache)
  if (!isVisible) {
    return null;
  }

  const text = t('home.visitorCounter.text');
  const formattedCount = displayCount.toLocaleString();
  
  // Split text by {count} placeholder and insert the number
  const parts = text.split('{count}');
  const beforeCount = parts[0];
  const afterCount = parts[1] || '';

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-8 bg-gradient-to-br from-gray-900/50 via-gray-800/50 to-black/50 dark:from-dark-primary/50 dark:via-dark-secondary/50 dark:to-dark-primary/50 backdrop-blur-sm"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <motion.p
            className="text-lg md:text-xl lg:text-2xl font-medium text-gray-300 dark:text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {beforeCount && <span>{beforeCount}</span>}
            <span className="mx-2 text-brand-orange-base font-bold">
              <span
                ref={countRef}
                className="inline-block tabular-nums text-2xl md:text-3xl lg:text-4xl"
              >
                {formattedCount}
              </span>
            </span>
            {afterCount && <span>{afterCount}</span>}
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default VisitorCounter;

